/**
 * GWP Engine - Gift With Purchase cart automation
 * Fetches rules from app proxy, evaluates cart state, auto-adds/removes gifts.
 */
(function () {
  'use strict';

  var GWP_PROPERTY = '_gwp_rule_id';
  var RULES_ENDPOINT = '/apps/gwp/rules';
  var CACHE_TTL = 60000; // 60s
  var COOLDOWN_MS = 200;
  var FETCH_TIMEOUT = 5000; // 5s
  var MAX_OPS_PER_MINUTE = 10;
  var state = {
    rules: null,
    rulesFetchedAt: 0,
    processing: false,
    cooldownTimer: null,
    opsThisMinute: 0,
    opsResetTimer: null,
    gwpFetch: false, // Flag to mark our own fetch calls
  };

  // ─── Utility helpers ───

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function fetchWithTimeout(url, options, timeout) {
    timeout = timeout || FETCH_TIMEOUT;
    state.gwpFetch = true;
    var result = Promise.race([
      fetch(url, options),
      new Promise(function (_, reject) {
        setTimeout(function () {
          reject(new Error('Timeout'));
        }, timeout);
      }),
    ]);
    return result.finally(function () {
      state.gwpFetch = false;
    });
  }

  function sequentialOps(items, fn) {
    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return fn(item);
      });
    }, Promise.resolve());
  }

  // ─── Rate limiting ───

  function canPerformOp() {
    if (state.opsThisMinute >= MAX_OPS_PER_MINUTE) {
      console.warn('[GWP] Rate limit reached (' + MAX_OPS_PER_MINUTE + ' ops/min). Skipping.');
      return false;
    }
    state.opsThisMinute++;
    if (!state.opsResetTimer) {
      state.opsResetTimer = setTimeout(function () {
        state.opsThisMinute = 0;
        state.opsResetTimer = null;
      }, 60000);
    }
    return true;
  }

  // ─── Rule fetching ───

  function fetchRules() {
    var now = Date.now();
    if (state.rules && now - state.rulesFetchedAt < CACHE_TTL) {
      return Promise.resolve(state.rules);
    }

    return fetchWithTimeout(RULES_ENDPOINT)
      .then(function (res) {
        if (!res.ok) throw new Error('GWP rules fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        state.rules = data.rules || [];
        state.rulesFetchedAt = Date.now();
        return state.rules;
      })
      .catch(function (err) {
        console.error('[GWP]', err);
        return state.rules || [];
      });
  }

  // ─── Cart helpers ───

  function getCart() {
    return fetchWithTimeout(window.theme.routes.cart + '.js').then(function (res) {
      return res.json();
    });
  }

  function addToCart(variantId, ruleId) {
    return fetchWithTimeout(window.theme.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: { _gwp_rule_id: ruleId },
          },
        ],
      }),
    }).then(function (res) {
      return res.json();
    });
  }

  function removeFromCart(lineKey) {
    return fetchWithTimeout('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    }).then(function (res) {
      return res.json();
    });
  }

  // ─── Gift detection ───

  function getCurrentGifts(cart) {
    var gifts = {};
    cart.items.forEach(function (item) {
      if (item.properties && item.properties[GWP_PROPERTY]) {
        gifts[item.properties[GWP_PROPERTY]] = {
          key: item.key,
          variantId: item.variant_id,
          productId: item.product_id,
        };
      }
    });
    return gifts;
  }

  function getCartTotalExcludingGifts(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      if (!item.properties || !item.properties[GWP_PROPERTY]) {
        total += item.final_line_price;
      }
    });
    return total / 100; // Convert from cents to currency
  }

  function getCartQuantityExcludingGifts(cart) {
    var qty = 0;
    cart.items.forEach(function (item) {
      if (!item.properties || !item.properties[GWP_PROPERTY]) {
        qty += item.quantity;
      }
    });
    return qty;
  }

  function cartHasProduct(cart, productId) {
    return cart.items.some(function (item) {
      if (item.properties && item.properties[GWP_PROPERTY]) return false;
      return String(item.product_id) === String(productId);
    });
  }

  function cartHasCollectionProduct(cart, collectionProductIds) {
    if (!collectionProductIds || !Array.isArray(collectionProductIds)) return false;
    return cart.items.some(function (item) {
      if (item.properties && item.properties[GWP_PROPERTY]) return false;
      return collectionProductIds.indexOf(item.product_id) !== -1;
    });
  }

  // ─── Rule evaluation ───

  function evaluateRule(rule, cart) {
    switch (rule.conditionType) {
      case 'CART_TOTAL':
        return getCartTotalExcludingGifts(cart) >= rule.thresholdAmount;

      case 'SPECIFIC_PRODUCT':
        return cartHasProduct(cart, rule.conditionProductId);

      case 'SPECIFIC_COLLECTION':
        return cartHasCollectionProduct(cart, rule.conditionCollectionProductIds);

      case 'QUANTITY':
        return getCartQuantityExcludingGifts(cart) >= rule.thresholdQuantity;

      default:
        return false;
    }
  }

  function getRemainingAmount(rule, cart) {
    if (rule.conditionType !== 'CART_TOTAL') return 0;
    var current = getCartTotalExcludingGifts(cart);
    return Math.max(0, rule.thresholdAmount - current);
  }

  // ─── Message rendering ───

  function renderMessages(rules, cart) {
    var containers = document.querySelectorAll('[data-gwp-messages]');
    if (containers.length === 0) return;

    var html = '';

    rules.forEach(function (rule) {
      var qualified = evaluateRule(rule, cart);
      var giftName = escapeHtml(rule.giftTitle || 'gift');

      if (qualified && rule.qualifiedMessage) {
        var msg = escapeHtml(rule.qualifiedMessage).replace(/\{gift\}/g, giftName);
        html +=
          '<div class="gwp-message gwp-message--qualified">' + msg + '</div>';
      } else if (!qualified && rule.progressMessage && rule.conditionType === 'CART_TOTAL') {
        var remaining = getRemainingAmount(rule, cart).toFixed(2);
        var msg = escapeHtml(rule.progressMessage)
          .replace(/\{remaining\}/g, remaining)
          .replace(/\{gift\}/g, giftName);
        html +=
          '<div class="gwp-message gwp-message--progress">' + msg + '</div>';
      }
    });

    containers.forEach(function (container) {
      container.innerHTML = html;
    });
  }

  // ─── Core engine ───

  function processCart(cart) {
    if (state.processing) return;
    if (!canPerformOp()) return;
    state.processing = true;

    fetchRules()
      .then(function (rules) {
        if (!rules || rules.length === 0) {
          state.processing = false;
          return;
        }

        var currentGifts = getCurrentGifts(cart);
        var toAdd = [];
        var toRemove = [];
        var nonStackableQualified = false;

        // Rules are already sorted by priority (desc) from the API
        rules.forEach(function (rule) {
          var qualified = evaluateRule(rule, cart);
          var hasGift = currentGifts[rule.id];

          if (qualified) {
            // Check stacking: if a non-stackable rule already qualified, skip
            if (nonStackableQualified && !rule.stackable) return;

            if (!hasGift) {
              toAdd.push(rule);
            }

            if (!rule.stackable) {
              nonStackableQualified = true;
            }
          } else if (hasGift) {
            toRemove.push(hasGift);
          }
        });

        // Also remove gifts for non-stackable blocking
        if (nonStackableQualified) {
          rules.forEach(function (rule) {
            var hasGift = currentGifts[rule.id];
            if (hasGift && !evaluateRule(rule, cart)) {
              if (toRemove.indexOf(hasGift) === -1) {
                toRemove.push(hasGift);
              }
            }
          });
        }

        // Render messages regardless of add/remove
        renderMessages(rules, cart);

        if (toAdd.length === 0 && toRemove.length === 0) {
          state.processing = false;
          return;
        }

        // Execute removes sequentially, then adds sequentially
        sequentialOps(toRemove, function (gift) {
          return removeFromCart(gift.key);
        })
          .then(function () {
            return sequentialOps(toAdd, function (rule) {
              return addToCart(rule.giftVariantId, rule.id);
            });
          })
          .then(function () {
            // Refresh cart state after changes
            return getCart();
          })
          .then(function (updatedCart) {
            // Re-render messages with updated cart
            renderMessages(rules, updatedCart);

            // Update header cart count/total
            document.dispatchEvent(
              new CustomEvent('theme:cart:change', {
                detail: { cart: updatedCart, _gwpOrigin: true },
                bubbles: true,
              })
            );
          })
          .catch(function (err) {
            console.error('[GWP] Cart operation failed:', err);
          })
          .finally(function () {
            // Cooldown to prevent rapid re-processing
            setTimeout(function () {
              state.processing = false;
            }, COOLDOWN_MS);
          });
      })
      .catch(function () {
        state.processing = false;
      });
  }

  // ─── Fetch interception ───
  // Intercept all cart mutation endpoints. No polling — every cart change
  // goes through one of these endpoints, so interception is sufficient.

  var nativeFetch = window.fetch;
  var lastCartToken = null;

  window.fetch = function () {
    var url = arguments[0];
    var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');

    var isCartMutation =
      urlStr.indexOf('/cart/add') !== -1 ||
      urlStr.indexOf('/cart/change') !== -1 ||
      urlStr.indexOf('/cart/update') !== -1 ||
      urlStr.indexOf('/cart/clear') !== -1;

    // Skip interception for our own requests or non-mutation requests
    if (state.gwpFetch || !isCartMutation) {
      return nativeFetch.apply(this, arguments);
    }

    // Let the mutation complete, then react
    return nativeFetch.apply(this, arguments).then(function (response) {
      // Only react to successful mutations
      if (response && response.ok) {
        setTimeout(function () {
          getCart().then(function (freshCart) {
            var token = freshCart.token + ':' + freshCart.item_count + ':' + freshCart.total_price;
            if (token !== lastCartToken) {
              lastCartToken = token;
              processCart(freshCart);
            }
          }).catch(function () {});
        }, 100);
      }
      return response;
    });
  };

  // ─── Event listeners ───

  document.addEventListener('theme:cart:change', function (event) {
    if (!event.detail || !event.detail.cart) return;
    if (event.detail._gwpOrigin) return; // Skip our own events

    // No debounce — react immediately
    processCart(event.detail.cart);
  });

  // ─── Initialise ───

  function shouldProcessInitialCart() {
    var themeState = window.theme || {};
    var initialCartCount = themeState.cartItemCount;

    if (window.__gwpForceInitialProcess) return true;
    if (themeState.templateName === 'cart') return true;
    if (typeof initialCartCount !== 'number') return true;

    return initialCartCount > 0;
  }

  function init() {
    if (!shouldProcessInitialCart()) return;
    window.__gwpForceInitialProcess = false;

    getCart().then(function (cart) {
      lastCartToken = cart.token + ':' + cart.item_count + ':' + cart.total_price;
      processCart(cart);
    });
  }

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }
})();
